import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, 
  FileText, 
  AlertCircle,
  ChevronRight,
  CheckCircle,
  TrendingUp,
  X
} from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import Modal from '../ui/Modal';

/**
 * Componente reutilizable para las pantallas previas antes de iniciar un examen
 * Incluye: Bienvenida, Instrucciones y Confirmación
 * 
 * @param {Object} props
 * @param {string} props.nombreProceso - Nombre del proceso
 * @param {string} props.nombreExamen - Nombre del examen
 * @param {string} props.descripcionExamen - Descripción del examen
 * @param {number} props.tiempoMinutos - Tiempo en minutos (0 = sin límite)
 * @param {number} props.cantidadPreguntas - Número de preguntas
 * @param {number} props.ponderacion - Porcentaje de ponderación
 * @param {string} props.tipoExamen - Tipo de examen (RAVEN, DISC, etc.)
 * @param {Function} props.onIniciar - Callback al iniciar el examen
 * @param {Function} props.onCancelar - Callback al cancelar
 */
const PantallasBienvenida = ({
  nombreProceso,
  nombreExamen,
  descripcionExamen,
  tiempoMinutos,
  cantidadPreguntas,
  ponderacion,
  tipoExamen = 'EXAMEN',
  onIniciar,
  onCancelar
}) => {
  const [paso, setPaso] = useState(1); // 1: Bienvenida, 2: Instrucciones, 3: Confirmación

  // Paso 1: Pantalla de Bienvenida
  if (paso === 1) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-4xl w-full"
        >
          <Card className="overflow-hidden">
            {/* Header con logo y título del proceso */}
            <div className="bg-gradient-to-r from-primary to-secondary text-white p-8 text-center">
              <div className="flex justify-center mb-6">
                <img 
                  src="/logopartner.png" 
                  alt="Logo BPO Peru" 
                  className="h-24 w-auto object-contain bg-white/10 backdrop-blur-sm rounded-2xl p-4"
                />
              </div>
              <h1 className="text-3xl font-bold mb-2">
                {nombreProceso}
              </h1>
              <p className="text-blue-100 text-lg">
                {new Date().toLocaleDateString('es-PE', { 
                  year: 'numeric', 
                  month: 'long' 
                }).toUpperCase()}
              </p>
            </div>

            {/* Contenido principal */}
            <div className="p-8">
              {/* Información del postulante */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 mb-8 border-2 border-blue-200">
                <h2 className="text-2xl font-bold text-neutral-900 mb-4">
                  Hola, este proceso incluye lo siguiente:
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <FileText className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600 font-medium">Cantidad de Pruebas:</p>
                      <p className="text-2xl font-bold text-neutral-900">{cantidadPreguntas}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-neutral-600 font-medium">Tiempo aproximado:</p>
                      <p className="text-2xl font-bold text-neutral-900">
                        {tiempoMinutos > 0 ? `${tiempoMinutos} min` : 'Sin límite'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Recomendaciones */}
                <div className="bg-white rounded-xl p-5 border border-blue-200 shadow-sm">
                  <h3 className="font-bold text-neutral-900 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-primary" />
                    Antes de iniciar el proceso, te recomendamos:
                  </h3>
                  <ul className="space-y-2 text-neutral-700">
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span>Buscar un lugar tranquilo y sin interrupciones.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span>Separa un tiempo prudente para resolver las pruebas sin cortarlas.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <ChevronRight className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                      <span>Sé sincero y mide tu tiempo.</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Advertencias importantes */}
              <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-300 rounded-xl p-5 mb-6">
                <h3 className="font-bold text-red-800 mb-3 flex items-center gap-2">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                  Lo que no debes hacer es lo siguiente:
                </h3>
                <ul className="space-y-2 text-neutral-800">
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>No abrir más de una pestaña</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>No cerrar el navegador durante tu evaluación</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>No iniciar sesión con la misma cuenta en otro navegador o dispositivo</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>No navegar en otras páginas web</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>No buscar o pedir ayuda de terceros</span>
                  </li>
                </ul>
              </div>

              {/* Advertencia de supervisión */}
              <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-2 border-yellow-400 rounded-xl p-5 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-8 h-8 text-yellow-700 flex-shrink-0" />
                  <div>
                    <p className="font-bold text-yellow-900 mb-2">
                      Recuerda que tu proceso está siendo supervisado, todo acto que no se debe realizar podría perjudicar tu evaluación y será penalizado.
                    </p>
                  </div>
                </div>
              </div>

              {/* Mensaje de motivación */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 mb-8 border border-green-200">
                <p className="text-center text-lg font-medium text-neutral-800">
                  👍 <strong>¡Relájate y da lo mejor de ti!</strong> Presiona en <strong className="text-success">ACEPTAR</strong> para comenzar.
                </p>
              </div>

              {/* Botones de acción */}
              <div className="flex items-center justify-center gap-4">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={onCancelar}
                  className="min-w-[140px]"
                >
                  <X className="w-5 h-5" />
                  Atrás
                </Button>
                <Button
                  variant="success"
                  size="lg"
                  onClick={() => setPaso(2)}
                  className="min-w-[140px] bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                >
                  Aceptar
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Paso 2: Instrucciones del examen
  if (paso === 2) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="max-w-5xl w-full"
        >
          <Card>
            {/* Header */}
            <div className="bg-gradient-to-r from-primary to-secondary text-white p-6">
              <h1 className="text-2xl font-bold text-center">Instrucciones</h1>
            </div>

            <div className="p-8">
              {/* Instrucciones específicas por tipo */}
              {tipoExamen === 'RAVEN' && (
                <div className="space-y-6">
                  <p className="text-neutral-700 text-lg leading-relaxed">
                    A continuación encontrará cierto número de situaciones y de preguntas seguidas de varias respuestas posibles.
                  </p>
                  
                  <p className="text-neutral-700 text-lg leading-relaxed">
                    Se le pide que escoja una alternativa para cada pregunta; la que de manera espontánea le parezca preferible.
                  </p>

                  <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                    <p className="text-neutral-800 font-medium mb-4">
                      En algunas cuestiones estará usted personalmente implicado, por ejemplo:
                    </p>
                    
                    <div className="bg-white rounded-lg p-5 mb-4 shadow-sm">
                      <p className="text-neutral-700 italic mb-3">
                        <strong>'Si usted pudiera elegir el tipo de vivienda de manera completamente libre, ¿Qué preferiría?'.</strong>
                      </p>
                      <ul className="space-y-2 text-neutral-600 ml-4">
                        <li className="flex items-start gap-2">
                          <span className="font-bold text-primary">a)</span>
                          <span>Una casa de campo con un gran jardín.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-bold text-primary">b)</span>
                          <span>Un lujoso apartamento en una gran ciudad.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-bold text-primary">c)</span>
                          <span>Una casa situada en una calle tranquila de una pequeña ciudad.</span>
                        </li>
                      </ul>
                    </div>

                    <p className="text-neutral-800 font-medium mb-4">
                      Otras cuestiones se refieren a personas ajenas como:
                    </p>

                    <div className="bg-white rounded-lg p-5 shadow-sm">
                      <p className="text-neutral-700 italic mb-3">
                        <strong>'J& está comprando en un gran almacén cuando advierte que alguien esconde discretamente un disco bajo su pantalón. ¿Qué hará J&?'.</strong>
                      </p>
                      <ul className="space-y-2 text-neutral-600 ml-4">
                        <li className="flex items-start gap-2">
                          <span className="font-bold text-primary">a)</span>
                          <span>Dar cuenta del hecho al primer vendedor que encuentre.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-bold text-primary">b)</span>
                          <span>No hacer nada porque piensa que no es cosa suya.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-bold text-primary">c)</span>
                          <span>Indicar al infractor con el gesto o la palabra que ha visto lo que ha hecho.</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <p className="text-neutral-700 text-lg leading-relaxed">
                    En este caso, usted contestará lo que según su propia opinión hará J&
                  </p>

                  <div className="bg-yellow-50 rounded-xl p-6 border-2 border-yellow-300">
                    <p className="text-neutral-800 font-bold text-center text-lg">
                      En realidad no hay respuestas 'buenas' o 'malas': cada uno piensa y actúa como cree conveniente en función de su carácter, intereses, etc.
                    </p>
                  </div>

                  <div className="bg-green-50 rounded-xl p-6 border border-green-300">
                    <p className="text-neutral-800 font-medium text-center text-lg">
                      Para ubicar sus respuestas debe seleccionar la alternativa que considere adecuada según su criterio en las opciones disponibles.
                    </p>
                  </div>

                  <div className="bg-blue-50 rounded-xl p-6 border border-blue-300">
                    <p className="text-neutral-800 font-medium text-center text-lg">
                      Responda lo más espontánea y sinceramente posible con las opciones propuestas; esfuércese entonces en elegir la que crea mejor entre las alternativas que se ofrecen.
                    </p>
                  </div>
                </div>
              )}

              {tipoExamen === 'DISC' && (
                <div className="space-y-6">
                  <p className="text-neutral-700 text-lg leading-relaxed">
                    A continuación encontrará grupos de palabras. Para cada grupo, deberá seleccionar:
                  </p>
                  
                  <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                    <ul className="space-y-3 text-neutral-800">
                      <li className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                          +
                        </div>
                        <div>
                          <strong>MÁS (+):</strong> La palabra que <strong>mejor te describe</strong> o con la que más te identificas
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                          -
                        </div>
                        <div>
                          <strong>MENOS (-):</strong> La palabra que <strong>menos te describe</strong> o con la que menos te identificas
                        </div>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 rounded-xl p-6 border-2 border-yellow-300">
                    <p className="text-neutral-800 font-bold text-center text-lg">
                      No hay respuestas correctas o incorrectas. Responde con sinceridad según cómo te percibes realmente.
                    </p>
                  </div>

                  <div className="bg-green-50 rounded-xl p-6 border border-green-300">
                    <p className="text-neutral-800 font-medium text-center text-lg">
                      Este test evalúa tu perfil de personalidad y comportamiento en diferentes situaciones.
                    </p>
                  </div>
                </div>
              )}

              {/* Instrucciones genéricas para otros tipos */}
              {tipoExamen !== 'RAVEN' && tipoExamen !== 'DISC' && (
                <div className="space-y-6">
                  <p className="text-neutral-700 text-lg leading-relaxed">
                    {descripcionExamen}
                  </p>
                  
                  <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                    <ul className="space-y-3 text-neutral-800">
                      <li className="flex items-start gap-2">
                        <ChevronRight className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span>Lee cuidadosamente cada pregunta antes de responder</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span>Selecciona la respuesta que consideres más apropiada</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ChevronRight className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                        <span>Puedes revisar y cambiar tus respuestas antes de enviar</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 rounded-xl p-6 border-2 border-yellow-300">
                    <p className="text-neutral-800 font-bold text-center text-lg">
                      Administra bien tu tiempo y responde todas las preguntas.
                    </p>
                  </div>
                </div>
              )}

              {/* Botones de navegación */}
              <div className="flex items-center justify-center gap-4 mt-8">
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setPaso(1)}
                  className="min-w-[140px]"
                >
                  <ChevronRight className="w-5 h-5 rotate-180" />
                  Atrás
                </Button>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => setPaso(3)}
                  className="min-w-[140px]"
                >
                  Siguiente
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Paso 3: Confirmación final con modal
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 flex items-center justify-center p-6">
      <Modal
        isOpen={true}
        onClose={() => setPaso(2)}
        title=""
        size="lg"
      >
        <div className="text-center py-8">
          <div className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
            <div className="w-24 h-24 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
              <img 
                src="/logopartner.png" 
                alt="Mascota BPO" 
                className="w-20 h-20 object-contain"
              />
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-3xl font-bold text-neutral-900 mb-3">
              ¡Felicitaciones!
            </h2>
            <p className="text-xl text-neutral-700 leading-relaxed">
              No encontramos ningún problema para guardar sus respuestas. 
              Puede empezar a realizar las evaluaciones.
            </p>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6 mb-6 border border-blue-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-sm text-neutral-600 mb-1">Pruebas</p>
                <p className="text-2xl font-bold text-primary">{cantidadPreguntas}</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-sm text-neutral-600 mb-1">Duración</p>
                <p className="text-2xl font-bold text-primary">
                  {tiempoMinutos > 0 ? `${tiempoMinutos} min` : 'Sin límite'}
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-sm text-neutral-600 mb-1">Tipo</p>
                <p className="text-xl font-bold text-primary">{tipoExamen}</p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-4">
            <Button
              variant="outline"
              size="lg"
              onClick={() => setPaso(2)}
              className="min-w-[140px]"
            >
              <ChevronRight className="w-5 h-5 rotate-180" />
              Atrás
            </Button>
            <Button
              variant="success"
              size="lg"
              onClick={onIniciar}
              className="min-w-[180px] bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
            >
              <CheckCircle className="w-5 h-5" />
              Finalizar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PantallasBienvenida;
